'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getConsentState, recordConsent } from '@/lib/tracking/consent';

/**
 * Cookie consent banner — v3 CNIL/EDPB equal-prominence redesign (design
 * contract `.claude/handoffs/tracking-v3-design.md`, decision 2).
 *
 * The pre-v3 version paired a filled "Accept" button against an outline
 * "Decline" — exactly the visual-prominence asymmetry the CNIL's €325M
 * Google fine targets (a technically-present reject option that reads, at a
 * glance, as the "wrong" choice). Both buttons below share the same size,
 * font-weight, and radius and differ ONLY in fill color, and the X button
 * genuinely dismisses without recording a choice — CNIL guidance also
 * treats "no way to just close the banner without choosing" as a
 * forced-choice dark pattern.
 *
 * All consent STATE now lives in `../lib/tracking/consent.ts` (this
 * component used to read/write the `cookie-accept` cookie directly with its
 * own `setCookie`/`getCookie` calls — that ownership moved there so every
 * consumer of consent state, including this banner, agrees on one source of
 * truth: `getConsentState()`/`recordConsent()`). GPC visitors never see this
 * banner — GPC IS an explicit, standards-based decline (design contract
 * decision 2: "GPC honored as auto-decline"), so re-asking would itself be
 * the dark pattern this redesign removes.
 */

const SNOOZE_KEY = 'cmg_consent_snooze';
const SHOW_DELAY_MS = 600;

type Locale = 'en' | 'ro';

const COPY: Record<
  Locale,
  {
    lead: string;
    linkLabel: string;
    accept: string;
    reject: string;
    dismiss: string;
    region: string;
  }
> = {
  en: {
    lead: 'We use essential cookies, and — only with your consent — analytics cookies to improve the site. See our',
    linkLabel: 'Cookie Policy',
    accept: 'Accept all',
    reject: 'Reject non-essential',
    dismiss: 'Dismiss cookie banner',
    region: 'Cookie consent',
  },
  ro: {
    lead: 'Folosim cookie-uri esențiale și, doar cu acordul tău, cookie-uri de analiză pentru a îmbunătăți site-ul. Vezi',
    linkLabel: 'Politica de cookie-uri',
    accept: 'Acceptă tot',
    reject: 'Respinge opționalele',
    dismiss: 'Închide bannerul de cookie-uri',
    region: 'Consimțământ cookie-uri',
  },
};

/** sessionStorage read for the X-dismiss snooze — see handleDismiss's docstring. */
function readSnoozed(): boolean {
  try {
    return sessionStorage.getItem(SNOOZE_KEY) === '1';
  } catch {
    // intentional: sessionStorage unavailable (private mode, storage quota) —
    // fail OPEN to showing the banner rather than silently never asking.
    return false;
  }
}

function writeSnoozed(): void {
  try {
    sessionStorage.setItem(SNOOZE_KEY, '1');
  } catch {
    // intentional: best-effort — worst case the banner re-appears on the next
    // page view within the same session, never a thrown error.
  }
}

/** RO copy only for a `navigator.language` starting with 'ro' (contract's exact rule). */
function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language?.toLowerCase().startsWith('ro') ? 'ro' : 'en';
}

const CookieConsent = () => {
  // `eligible`: this browser has no recorded choice yet, isn't sending GPC,
  // and wasn't X-dismissed already this session — decided ONCE, synchronously,
  // in the mount effect below. `visible`: the actual slide-up+fade reveal,
  // flipped `SHOW_DELAY_MS` later. Splitting these (rather than one boolean)
  // is what makes the entrance a real CSS transition: the banner mounts
  // in-DOM (but aria-hidden + non-interactive) in its "before" position the
  // instant it's eligible, so the class flip at the delay is a genuine state
  // change on an already-rendered element, not a first paint already in its
  // final position.
  const [eligible, setEligible] = useState(false);
  const [visible, setVisible] = useState(false);
  const [locale, setLocale] = useState<Locale>('en');

  useEffect(() => {
    const state = getConsentState();
    if (state !== 'unset') return; // accepted/declined/gpc -> never (re-)ask
    if (readSnoozed()) return; // X-dismissed already this session -> re-ask next session

    // Intentional: standard hydration-safety mount guard; getConsentState/detectLocale read
    // client-only APIs (cookie, sessionStorage, navigator.language) that can't run during SSR,
    // so the eligibility + locale decision can only be made once, synchronously, after mount.
    /* eslint-disable-next-line react-hooks/set-state-in-effect */
    setLocale(detectLocale());
    setEligible(true);

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!eligible) return null;

  // All three handlers below fully retire the banner (`setEligible(false)`,
  // not just `setVisible(false)`) — once a choice is made (or snoozed) there
  // is no scenario where THIS mounted instance needs to show again; leaving
  // it merely invisible would keep a dead, pointer-events-none node around
  // for the rest of the page session for no benefit.

  function handleAccept(): void {
    recordConsent('accepted');
    setVisible(false);
    setEligible(false);
  }

  function handleReject(): void {
    recordConsent('declined');
    setVisible(false);
    setEligible(false);
  }

  /**
   * X = dismiss WITHOUT recording a choice (design contract decision 2) —
   * deliberately NOT `recordConsent()`. This changes the pre-v3 semantics on
   * purpose: X used to silently write 'declined'. sessionStorage means this
   * session won't re-ask, but the next session (or a hard refresh after this
   * one ends) does, until an explicit accept/reject is made.
   */
  function handleDismiss(): void {
    writeSnoozed();
    setVisible(false);
    setEligible(false);
  }

  const copy = COPY[locale];

  return (
    <div
      data-cookie-consent
      role="region"
      aria-label={copy.region}
      aria-hidden={!visible}
      className={cn(
        'fixed z-50 inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:max-w-sm w-full',
        // transform/opacity only (compositor-friendly) — never `transition: all`.
        'transition-[transform,opacity] duration-500 ease-out',
        // Reduced motion: keep the fade, drop the slide, shorten the duration —
        // same convention as LandingNavbar's scroll show/hide (never a dead or
        // instant snap; `motion-reduce:transform-none` wins over the
        // conditional translate-y-* below regardless of `visible`).
        'motion-reduce:transform-none motion-reduce:duration-150',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 pointer-events-none'
      )}
    >
      <div className="relative rounded-t-2xl sm:rounded-2xl glass bg-black/80 border border-yellow-500/20 shadow-2xl p-4 pr-10">
        <button
          type="button"
          onClick={handleDismiss}
          tabIndex={visible ? 0 : -1}
          aria-label={copy.dismiss}
          className="absolute right-3 top-3 rounded-full p-1 text-gray-400 hover:text-yellow-500 hover:bg-yellow-500/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        <p className="text-sm text-gray-300 leading-relaxed">
          {copy.lead}{' '}
          <Link href="/legal/cookies" className="text-yellow-500 hover:text-yellow-400 underline">
            {copy.linkLabel}
          </Link>
          .
        </p>

        {/* Equal-prominence pair (design contract decision 2): identical size
            (h-9, flex-1), font-weight (font-medium), and radius (rounded-md) —
            differ ONLY in fill color (+ the text color contrast that fill
            requires), never in variant/border/outline treatment. */}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleReject}
            tabIndex={visible ? 0 : -1}
            className="flex-1 h-9 rounded-md text-sm font-medium bg-white/10 text-white hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            {copy.reject}
          </button>
          <button
            type="button"
            onClick={handleAccept}
            tabIndex={visible ? 0 : -1}
            className="flex-1 h-9 rounded-md text-sm font-medium bg-yellow-500 text-black hover:bg-yellow-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            {copy.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
